import { useState, useCallback, useRef, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { Conversation, Message, SineModel, AppSettings, StudioConversationState } from '../types'
import { generateTitle } from '../lib/utils'
import { useAuth } from '../hooks/useAuth'
import { apiFetch } from '../lib/api'
import { isConnectorReady } from '@/shared/connectors/availability'

import { API_BASE } from '@/lib/config'

async function generateAITitle(message: string, language = 'no'): Promise<string> {
  try {
    const res = await fetch(`${API_BASE}/api/chat/generate-title`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, language }),
    })
    if (!res.ok) throw new Error('API error')
    const data = await res.json()
    const title = typeof data.title === 'string' ? data.title.trim() : ''
    return title && !/^ny samtale$/i.test(title) ? title : generateTitle(message)
  } catch {
    return generateTitle(message)
  }
}

import {
  fetchConversations,
  fetchMessagesForConversation,
  createConversationInDb,
  updateConversationTitle,
  updateConversationAppMode,
  updateConversationModelInDb,
  updateConversationStudioState as persistConversationStudioState,
  updateConversationCreditsUsed as persistConversationCreditsUsed,
  deleteConversationFromDb,
  insertMessage,
  updateMessageContent,
  toggleFavoriteInDb,
  buildMessageMetadata,
} from '../services/conversationService'

const studioStatePersistTimers = new Map<string, number>()
const studioStatePersistPayloads = new Map<string, string>()

const DEFAULT_SETTINGS: AppSettings = {
  language: 'no',
  model: 'sine-lite',
  theme: 'dark',
}

// ── Settings: still localStorage (not sensitive) ─────────────
function settingsKey(userId?: string | null): string {
  return userId ? `sine_settings_${userId}` : 'sine_settings'
}
function loadSettings(userId?: string | null): AppSettings {
  try {
    const raw = localStorage.getItem(settingsKey(userId))
    if (!raw) return DEFAULT_SETTINGS
    // Merge with DEFAULT_SETTINGS so old users missing fields (e.g. language) get defaults
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } as AppSettings
  } catch {
    return DEFAULT_SETTINGS
  }
}
function saveSettings(s: AppSettings, userId?: string | null) {
  try {
    localStorage.setItem(settingsKey(userId), JSON.stringify(s))
  } catch { /* ignore */ }
}

async function fetchConnectorCatalog(): Promise<AppSettings['connectorCapabilities']> {
  const res = await apiFetch(`${API_BASE}/api/connectors/catalog`)
  if (!res.ok) throw new Error('Failed to fetch connector catalog')
  const data = await res.json()
  return data.connectors ?? {}
}

function conversationsCacheKey(userId: string): string {
  return `sine_conversations_cache_${userId}`
}

function conversationMessagesCacheKey(userId: string, conversationId: string): string {
  return `sine_conversation_messages_${userId}_${conversationId}`
}

function serializeConversationMeta(conversation: Conversation) {
  return {
    id: conversation.id,
    title: conversation.title,
    createdAt: conversation.createdAt.toISOString(),
    updatedAt: conversation.updatedAt.toISOString(),
    model: conversation.model,
    type: conversation.type,
    agentType: conversation.agentType ?? null,
    isFavorite: conversation.isFavorite ?? false,
    creditsUsed: conversation.creditsUsed ?? 0,
    projectId: conversation.projectId ?? null,
    appMode: conversation.appMode ?? 'chat',
    useAgentMode: conversation.useAgentMode ?? false,
    enabledConnectors: conversation.enabledConnectors ?? null,
    studioState: conversation.studioState ?? null,
  }
}

function serializeMessage(message: Message) {
  return {
    ...message,
    createdAt: message.createdAt.toISOString(),
  }
}

function deserializeMessage(raw: Record<string, unknown>): Message {
  return {
    ...(raw as unknown as Message),
    createdAt: new Date(String(raw.createdAt ?? Date.now())),
  }
}

function messageRolePriority(role: Message['role']): number {
  switch (role) {
    case 'user':
      return 0
    case 'assistant':
      return 1
    case 'agent':
      return 2
    default:
      return 3
  }
}

function sortMessagesChronologically(messages: Message[]): Message[] {
  return [...messages].sort((left, right) => {
    const timeDiff = left.createdAt.getTime() - right.createdAt.getTime()
    if (timeDiff !== 0) return timeDiff

    const roleDiff = messageRolePriority(left.role) - messageRolePriority(right.role)
    if (roleDiff !== 0) return roleDiff

    return left.id.localeCompare(right.id)
  })
}

function loadCachedConversations(userId: string): Conversation[] {
  try {
    const raw = localStorage.getItem(conversationsCacheKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as Array<Record<string, unknown>>
    if (!Array.isArray(parsed)) return []
    return parsed.map(item => ({
      id: String(item.id),
      title: String(item.title ?? 'Ny samtale'),
      messages: [],
      createdAt: new Date(String(item.createdAt ?? Date.now())),
      updatedAt: new Date(String(item.updatedAt ?? Date.now())),
      model: (item.model as SineModel) ?? 'sine-lite',
      type: (item.type as 'chat' | 'agent') ?? 'chat',
      agentType: (item.agentType as 'code' | 'writing' | null) ?? undefined,
      isFavorite: Boolean(item.isFavorite),
      creditsUsed: Number(item.creditsUsed ?? 0),
      projectId: (item.projectId as string | null) ?? null,
      appMode: (item.appMode as 'chat' | 'studio') ?? 'chat',
      useAgentMode: Boolean(item.useAgentMode),
      enabledConnectors: Array.isArray(item.enabledConnectors)
        ? item.enabledConnectors.map(String)
        : undefined,
      studioState: (item.studioState as StudioConversationState | null) ?? null,
    }))
  } catch {
    return []
  }
}

function loadCachedMessages(userId: string, conversationId: string): Message[] {
  try {
    const raw = localStorage.getItem(conversationMessagesCacheKey(userId, conversationId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as Array<Record<string, unknown>>
    if (!Array.isArray(parsed)) return []
    return sortMessagesChronologically(parsed.map(deserializeMessage))
  } catch {
    return []
  }
}

function saveCachedConversations(userId: string, conversations: Conversation[]): void {
  try {
    localStorage.setItem(
      conversationsCacheKey(userId),
      JSON.stringify(conversations.map(serializeConversationMeta)),
    )
  } catch {
    // ignore cache failures
  }
}

function saveCachedMessages(userId: string, conversationId: string, messages: Message[]): void {
  try {
    localStorage.setItem(
      conversationMessagesCacheKey(userId, conversationId),
      JSON.stringify(messages.map(serializeMessage)),
    )
  } catch {
    // ignore cache failures
  }
}

function removeCachedMessages(userId: string, conversationId: string): void {
  try {
    localStorage.removeItem(conversationMessagesCacheKey(userId, conversationId))
  } catch {
    // ignore cache failures
  }
}

// ── Store ─────────────────────────────────────────────────────
export function useAppStore() {
  const { user } = useAuth()

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [conversationsLoaded, setConversationsLoaded] = useState(false)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  // Track which conversations have had their messages lazy-loaded
  const messagesLoadedRef = useRef<Set<string>>(new Set())
  const messagesLoadingRef = useRef<Set<string>>(new Set())
  const [activeMessagesLoading, setActiveMessagesLoading] = useState(false)

  const [settings, setSettings] = useState<AppSettings>(() => loadSettings(null))
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [userClosedSidebar, setUserClosedSidebar] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsInitialTab, setSettingsInitialTab] = useState<string | undefined>(undefined)
  const [activeProjectInstructions, setActiveProjectInstructions] = useState<string | null>(null)

  // Keep userId in a ref so async callbacks can read it without stale closures
  const userIdRef = useRef<string | null>(null)
  useEffect(() => {
    userIdRef.current = user?.id ?? null
  }, [user?.id, user?.name, user?.customInstructions])

  // ── Track pending DB conversation creations to avoid FK race ──
  const pendingConvCreates = useRef<Map<string, Promise<void>>>(new Map())

  const persistConversationTitle = useCallback((conversationId: string, title: string) => {
    const pending = pendingConvCreates.current.get(conversationId)
    if (pending) {
      pending.then(() => updateConversationTitle(conversationId, title)).catch(console.error)
      return
    }
    updateConversationTitle(conversationId, title).catch(console.error)
  }, [])

  const hydrateConversationTitle = useCallback((conversationId: string, seedMessage: string) => {
    const fallbackTitle = generateTitle(seedMessage)

    setConversations(prev => prev.map(c =>
      c.id === conversationId && /^ny samtale$/i.test(c.title)
        ? { ...c, title: fallbackTitle }
        : c
    ))
    persistConversationTitle(conversationId, fallbackTitle)

    const langForTitle = loadSettings(userIdRef.current).language ?? 'no'
    generateAITitle(seedMessage, langForTitle).then(aiTitle => {
      if (!aiTitle || aiTitle === fallbackTitle) return
      setConversations(prev => prev.map(c =>
        c.id === conversationId ? { ...c, title: aiTitle } : c
      ))
      persistConversationTitle(conversationId, aiTitle)
    }).catch(console.error)
  }, [persistConversationTitle])

  // ── Load conversations from Supabase when user changes ───────
  // Only fetches conversation metadata (no messages) — messages are lazy-loaded
  useEffect(() => {
    if (!user?.id) {
      setConversations([])
      setActiveConversationId(null)
      setConversationsLoaded(false)
      setSettings(loadSettings(null))
      messagesLoadedRef.current = new Set()
      messagesLoadingRef.current = new Set()
      return
    }

    // Reload settings scoped to this user
    const loadedSettings = loadSettings(user.id)
    const mergedSettings: AppSettings = {
      ...loadedSettings,
      ...(user.name ? { userName: user.name } : {}),
      ...(user.customInstructions ? { customInstructions: user.customInstructions } : {}),
    }
    setSettings(mergedSettings)
    saveSettings(mergedSettings, user.id)

    setConversationsLoaded(false)
    messagesLoadedRef.current = new Set()
    messagesLoadingRef.current = new Set()

    const cachedConversations = loadCachedConversations(user.id)
    if (cachedConversations.length > 0) {
      setConversations(cachedConversations)
      setConversationsLoaded(true)
    }

    fetchConversations(user.id)
      .then(convs => {
        setConversations(convs)
        setConversationsLoaded(true)
        // Messages are lazy-loaded when a conversation is opened.
        // Title hydration happens after messages load in openConversation.
      })
      .catch(() => {
        setConversationsLoaded(true)
      })
  }, [user?.customInstructions, user?.id, user?.name])

  useEffect(() => {
    if (!user?.id) return

    fetchConnectorCatalog()
      .then(catalog => {
        setSettings(prev => {
          const nextStatuses = Object.fromEntries(
            Object.entries(catalog).map(([id, capability]) => [
              id,
              isConnectorReady(capability) ? 'connected' : 'disconnected',
            ])
          ) as NonNullable<AppSettings['connectorStatuses']>

          const updated = {
            ...prev,
            connectorCapabilities: catalog,
            connectorStatuses: {
              ...(prev.connectorStatuses ?? {}),
              ...nextStatuses,
            },
          }

          saveSettings(updated, user.id)
          return updated
        })
      })
      .catch(console.error)
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    const timer = window.setTimeout(() => {
      saveCachedConversations(user.id, conversations)
      for (const conversation of conversations) {
        if (conversation.messages.length > 0) {
          saveCachedMessages(user.id, conversation.id, conversation.messages)
        }
      }
    }, 600)
    return () => window.clearTimeout(timer)
  }, [conversations, user?.id])

  const openSettingsTab = useCallback((tab?: string) => {
    setSettingsInitialTab(tab)
    setSettingsOpen(true)
  }, [])

  const activeConversation = conversations.find(c => c.id === activeConversationId) ?? null

  // ── Lazy-load messages when a conversation is opened ─────────
  // Use this instead of setActiveConversationId directly.
  const openConversation = useCallback((id: string | null) => {
    setActiveConversationId(id)
    if (!id) {
      setActiveMessagesLoading(false)
      return
    }

    // Already loaded — nothing to do
    if (messagesLoadedRef.current.has(id)) {
      setActiveMessagesLoading(false)
      return
    }

    // Already in flight — just update loading indicator
    if (messagesLoadingRef.current.has(id)) {
      setActiveMessagesLoading(true)
      return
    }

    // Start loading
    messagesLoadingRef.current.add(id)
    const uid = userIdRef.current
    const cachedMessages = uid ? loadCachedMessages(uid, id) : []
    if (cachedMessages.length > 0) {
      setConversations(prev => prev.map(c =>
        c.id === id ? { ...c, messages: cachedMessages } : c
      ))
      setActiveMessagesLoading(false)
    } else {
      setActiveMessagesLoading(true)
    }

    fetchMessagesForConversation(id)
      .then(messages => {
        messagesLoadedRef.current.add(id)
        messagesLoadingRef.current.delete(id)

        setConversations(prev => prev.map(c =>
          c.id === id ? { ...c, messages } : c
        ))
        setActiveMessagesLoading(false)
        if (uid) {
          saveCachedMessages(uid, id, messages)
        }

        // Hydrate title if still 'Ny samtale'
        const seedMsg = messages.find(m => m.role === 'user' && m.content.trim())
        if (seedMsg) {
          setConversations(prev => {
            const conv = prev.find(c => c.id === id)
            if (conv && /^ny samtale$/i.test(conv.title)) {
              hydrateConversationTitle(id, seedMsg.content)
            }
            return prev
          })
        }
      })
      .catch(err => {
        console.error('[store] Failed to load messages for conversation', id, err)
        messagesLoadingRef.current.delete(id)
        setActiveMessagesLoading(false)
      })
  }, [hydrateConversationTitle])

  // ── Create conversation ───────────────────────────────────────
  const createConversation = useCallback((model: SineModel = DEFAULT_SETTINGS.model, appMode?: 'chat' | 'studio'): string => {
    const id = uuidv4()
    const now = new Date()
    const conv: Conversation = {
      id,
      title: 'Ny samtale',
      messages: [],
      createdAt: now,
      updatedAt: now,
      model,
      appMode: appMode ?? 'chat',
    }
    setConversations(prev => [conv, ...prev])
    setActiveConversationId(id)
    // Mark as loaded immediately — messages start empty and are added via addMessage
    messagesLoadedRef.current.add(id)
    setActiveMessagesLoading(false)

    const uid = userIdRef.current
    if (uid) {
      const p = createConversationInDb(uid, id, 'Ny samtale', 'chat', undefined, appMode ?? 'chat')
        .catch(console.error)
        .finally(() => pendingConvCreates.current.delete(id)) as Promise<void>
      pendingConvCreates.current.set(id, p)
    }

    return id
  }, [])

  // ── Add message ───────────────────────────────────────────────
  const addMessage = useCallback((conversationId: string, message: Omit<Message, 'id' | 'createdAt'>): string => {
    const id = uuidv4()
    const now = new Date()
    const newMsg: Message = { ...message, id, createdAt: now }

    let titleChanged = false

    setConversations(prev => {
      let foundConversation = false
      const next = prev.map(c => {
        if (c.id !== conversationId) return c
        foundConversation = true
        const msgs = [...c.messages, newMsg]
        const title = c.title === 'Ny samtale' && message.role === 'user'
          ? generateTitle(message.content)
          : c.title
        if (title !== c.title) {
          titleChanged = true
        }
        return { ...c, messages: msgs, updatedAt: now, title }
      })
      if (!foundConversation) {
        console.warn('[store] Tried to add message to missing conversation', { conversationId, role: message.role })
      }
      return next
    })

    if (titleChanged) {
      hydrateConversationTitle(conversationId, message.content)
    }

    if (!message.isStreaming) {
      const pending = pendingConvCreates.current.get(conversationId)
      if (pending) {
        pending.then(() => insertMessage(conversationId, newMsg)).catch(console.error)
      } else {
        insertMessage(conversationId, newMsg).catch(console.error)
      }
    }

    return id
  }, [hydrateConversationTitle])

  // ── Update message (called during streaming + on finish) ─────
  const updateMessage = useCallback((conversationId: string, messageId: string, content: string, isStreaming = false) => {
    let msgCreatedAt: Date = new Date()
    let msgRole = 'assistant'

    setConversations(prev => {
      return prev.map(c => {
        if (c.id !== conversationId) return c
        return {
          ...c,
          messages: c.messages.map(m => {
            if (m.id !== messageId) return m
            msgCreatedAt = m.createdAt
            msgRole = m.role
            return { ...m, content, isStreaming }
          }),
          updatedAt: new Date(),
        }
      })
    })

    if (!isStreaming) {
      updateMessageContent(messageId, content, undefined, {
        conversationId,
        role: msgRole,
        createdAt: msgCreatedAt,
      }).catch(console.error)
    }
  }, [])

  // ── Update agent message (tasks, files, plan, events) ────────
  const updateAgentMessage = useCallback((
    conversationId: string,
    messageId: string,
    updates: Partial<Message>
  ) => {
    let persistedContent = updates.content ?? ''
    let msgCreatedAt: Date = new Date()
    let msgRole: Message['role'] = 'agent'
    let mergedMessage: Message | null = null

    setConversations(prev => {
      return prev.map(c => {
        if (c.id !== conversationId) return c
        return {
          ...c,
          messages: c.messages.map(m => {
            if (m.id !== messageId) return m
            msgCreatedAt = m.createdAt
            msgRole = m.role
            persistedContent = updates.content ?? m.content
            mergedMessage = { ...m, ...updates }
            return mergedMessage
          }),
          updatedAt: new Date(),
        }
      })
    })

    const meta = mergedMessage ? buildMessageMetadata(mergedMessage) : {}

    if (updates.content !== undefined || Object.keys(meta).length > 0) {
      const persist = () => updateMessageContent(
        messageId,
        persistedContent,
        Object.keys(meta).length > 0 ? meta : undefined,
        {
          conversationId,
          role: msgRole,
          createdAt: msgCreatedAt,
        }
      ).catch(console.error)

      const pending = pendingConvCreates.current.get(conversationId)
      if (pending) {
        pending.then(persist).catch(console.error)
      } else {
        persist()
      }
    }
  }, [])

  // ── Delete conversation ───────────────────────────────────────
  const deleteConversation = useCallback((id: string) => {
    setConversations(prev => prev.filter(c => c.id !== id))
    if (activeConversationId === id) {
      setActiveConversationId(null)
      setActiveMessagesLoading(false)
    }
    messagesLoadedRef.current.delete(id)
    messagesLoadingRef.current.delete(id)
    if (userIdRef.current) {
      removeCachedMessages(userIdRef.current, id)
    }
    deleteConversationFromDb(id).catch(console.error)
  }, [activeConversationId])

  // ── Toggle favourite ──────────────────────────────────────────
  const toggleFavorite = useCallback((id: string) => {
    let newVal = false
    setConversations(prev => prev.map(c => {
      if (c.id !== id) return c
      newVal = !c.isFavorite
      return { ...c, isFavorite: newVal }
    }))
    toggleFavoriteInDb(id, newVal).catch(console.error)
  }, [])

  // ── Rename conversation ───────────────────────────────────────
  const renameConversation = useCallback((id: string, newTitle: string) => {
    if (!newTitle.trim()) return
    setConversations(prev => prev.map(c =>
      c.id === id ? { ...c, title: newTitle.trim() } : c
    ))
    persistConversationTitle(id, newTitle.trim())
  }, [persistConversationTitle])

  // ── Update conversation model ────────────────────────────────
  const updateConversationModel = useCallback((id: string, model: SineModel) => {
    setConversations(prev => prev.map(c =>
      c.id === id ? { ...c, model } : c
    ))
    updateConversationModelInDb(id, model).catch(console.error)
  }, [])

  // ── Update conversation metadata (per-chat settings) ──────────
  const updateConversationMeta = useCallback((id: string, updates: { enabledConnectors?: string[]; useAgentMode?: boolean; appMode?: 'chat' | 'studio' }) => {
    setConversations(prev => prev.map(c =>
      c.id === id ? { ...c, ...updates } : c
    ))
    if (updates.useAgentMode === true) {
      updateConversationAppMode(id, 'agent').catch(console.error)
    } else if (updates.appMode) {
      updateConversationAppMode(id, updates.appMode).catch(console.error)
    } else if (updates.useAgentMode === false) {
      updateConversationAppMode(id, 'chat').catch(console.error)
    }
  }, [])

  const updateConversationStudioState = useCallback((id: string, studioState: StudioConversationState | null) => {
    setConversations(prev => prev.map(c => {
      if (c.id !== id) return c
      return { ...c, studioState }
    }))
    const nextSerialized = JSON.stringify(studioState ?? null)
    if (studioStatePersistPayloads.get(id) === nextSerialized) return
    studioStatePersistPayloads.set(id, nextSerialized)

    const existingTimer = studioStatePersistTimers.get(id)
    if (existingTimer) {
      window.clearTimeout(existingTimer)
    }

    const timer = window.setTimeout(() => {
      persistConversationStudioState(id, studioState).catch(console.error)
      studioStatePersistTimers.delete(id)
    }, 350)

    studioStatePersistTimers.set(id, timer)
  }, [])

  const setConversationCreditsUsed = useCallback((id: string, creditsUsed: number) => {
    setConversations(prev => prev.map(c => (
      c.id === id ? { ...c, creditsUsed } : c
    )))
    persistConversationCreditsUsed(id, Math.max(0, Math.round(creditsUsed))).catch(console.error)
  }, [])

  const incrementConversationCreditsUsed = useCallback((id: string, delta: number) => {
    if (!Number.isFinite(delta) || delta <= 0) return
    let found = false
    let nextCreditsUsed = 0
    setConversations(prev => prev.map(c => {
      if (c.id !== id) return c
      found = true
      nextCreditsUsed = (c.creditsUsed ?? 0) + delta
      return { ...c, creditsUsed: nextCreditsUsed }
    }))
    if (!found) return
    persistConversationCreditsUsed(id, Math.max(0, Math.round(nextCreditsUsed))).catch(console.error)
  }, [])

  // ── Settings ──────────────────────────────────────────────────
  const updateSettings = useCallback((updates: Partial<AppSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...updates }
      saveSettings(updated, userIdRef.current)
      return updated
    })
  }, [])

  return {
    conversations,
    conversationsLoaded,
    activeConversation,
    activeConversationId,
    // Use openConversation instead of setActiveConversationId for lazy message loading
    setActiveConversationId: openConversation,
    openConversation,
    activeMessagesLoading,
    createConversation,
    addMessage,
    updateMessage,
    updateAgentMessage,
    deleteConversation,
    toggleFavorite,
    renameConversation,
    updateConversationMeta,
    updateConversationModel,
    updateConversationStudioState,
    setConversationCreditsUsed,
    incrementConversationCreditsUsed,
    settings,
    updateSettings,
    sidebarOpen,
    setSidebarOpen,
    userClosedSidebar,
    setUserClosedSidebar,
    settingsOpen,
    setSettingsOpen,
    settingsInitialTab,
    setSettingsInitialTab,
    openSettingsTab,
    activeProjectInstructions,
    setActiveProjectInstructions,
  }
}

export type AppStore = ReturnType<typeof useAppStore>
